import { useState } from "react";
import { Rocket, CalendarDays, LibraryBig, Music2, ListMusic, HandCoins, FolderOpen, Package, Bell, ShieldCheck, ChevronDown, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { HELP_SECTIONS } from "@/lib/helpContent";
import { Button } from "@/components/ui/button";

const ICONS = { Rocket, CalendarDays, LibraryBig, Music2, ListMusic, HandCoins, FolderOpen, Package, Bell, ShieldCheck };
const MANAGE_ROLES = ["admin", "music_leader", "treasurer"];

// Centre d'aide permanent : guides détaillés par rubrique, filtrés selon le rôle,
// + relecture du tutoriel de démarrage (événement écouté par Layout).
export default function HelpCenterPage() {
  const { t, user } = useAuth();
  const lang = localStorage.getItem("rannea_lang") === "en" ? "en" : "fr";
  const [openId, setOpenId] = useState("start");
  const sections = HELP_SECTIONS.filter((s) => s.minRole === "all" || MANAGE_ROLES.includes(user?.role));

  const replayTutorial = () => window.dispatchEvent(new CustomEvent("rannea:replay-tutorial"));
  const startTour = () => window.dispatchEvent(new CustomEvent("rannea:start-tour"));

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4 pb-10 min-w-0" data-testid="help-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="help-title">{t("help.title")}</h1>
        <p className="t-secondary text-sm mt-1" data-testid="help-subtitle">{t("help.subtitle")}</p>
      </div>

      <div className="app-card p-4 flex items-center gap-3" data-testid="help-replay-card">
        <span className="h-10 w-10 rounded-xl brand-bg/15 brand flex items-center justify-center shrink-0">
          <GraduationCap className="h-5 w-5" />
        </span>
        <p className="text-sm flex-1 min-w-0">{t("help.replay_hint")}</p>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Button size="sm" className="brand-bg text-white" onClick={startTour} data-testid="help-tour-button">{t("help.guided_tour")}</Button>
          <Button size="sm" variant="outline" className="border-app" onClick={replayTutorial} data-testid="help-replay-button">{t("help.replay")}</Button>
        </div>
      </div>

      <div className="space-y-2.5">
        {sections.map((s) => {
          const Icon = ICONS[s.icon] || Rocket;
          const c = s[lang];
          const open = openId === s.id;
          return (
            <div key={s.id} className="app-card overflow-hidden" data-testid={`help-section-${s.id}`}>
              <button
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors"
                onClick={() => setOpenId(open ? null : s.id)}
                data-testid={`help-section-toggle-${s.id}`}
              >
                <span className="h-9 w-9 rounded-lg brand-bg/15 brand flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex-1 min-w-0 font-semibold text-sm sm:text-base">{c.title}</span>
                <ChevronDown className={`h-4 w-4 t-secondary shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <ul className="px-4 pb-4 pt-1 space-y-2.5 anim-fade-up" data-testid={`help-section-body-${s.id}`}>
                  {c.points.map((p, i) => (
                    <li key={i} className="text-sm t-secondary leading-relaxed flex gap-2.5">
                      <span className="accent mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 accent-bg" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
